import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { Button } from '@/components/ui/Button'
import { useTwoFactorVerifyLogin } from '@/features/auth/hooks/useAuth'
import { AuthLayout } from '@/layouts/AuthLayout'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  code: z.string().min(1, 'Enter your authenticator code or a recovery code'),
})

type FormValues = z.infer<typeof schema>

/**
 * Reached mid-login, before a session exists — the twoFactorToken is passed
 * via router state (not the URL) by LoginPage. `code` accepts either a
 * 6-digit TOTP code or a recovery code (backend distinguishes internally).
 */
export function TwoFactorChallengePage() {
  const navigate = useNavigate()
  const location = useLocation()
  const twoFactorToken = (location.state as { twoFactorToken?: string } | null)?.twoFactorToken
  const verify = useTwoFactorVerifyLogin()
  const [apiError, setApiError] = useState<unknown>(null)

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '' },
  })

  if (!twoFactorToken) {
    return <Navigate to="/login" replace />
  }

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    verify.mutate(
      { twoFactorToken, code: values.code },
      {
        onSuccess: () => void navigate('/', { replace: true }),
        onError: (error) => setApiError(error),
      },
    )
  })

  return (
    <AuthLayout
      title="Two-Factor Verification"
      subtitle="Enter the code from your authenticator app"
    >
      <FormErrorSummary
        errors={errors}
        serverErrors={getFieldErrors(apiError)}
        generalMessage={apiError ? getErrorMessage(apiError) : null}
      />
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <FormInput control={control} name="code" label="Authentication Code" required />
        <Button type="submit" className="w-100" loading={verify.isPending}>
          Verify
        </Button>
      </form>
      <div className="text-center mt-3">
        <p className="text-muted mb-0 fs-12">
          Lost access? Use one of your saved recovery codes above.
        </p>
      </div>
    </AuthLayout>
  )
}
